import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  BellIcon,
  Button,
  CampaignIcon,
  Card,
  CheckCircleIcon,
  ClockIcon,
  Cluster,
  EmptyState,
  Field,
  Input,
  Inspector,
  Metric,
  Modal,
  Page,
  QueryState,
  RocketIcon,
  SegmentBar,
  Select,
  ShieldIcon,
  Stack,
  StatStrip,
  Table,
  cx,
  type Column,
  type InspectorField,
  type Tone,
} from '@integra/ui';
import {
  useAssets,
  useCampaigns,
  useConnectorConfigs,
  useCreateCampaign,
  useDestinations,
} from '../api/hooks.js';
import type { Campaign } from '../api/types.js';
import { REVIEW_PUBLISH_TEMPLATE } from '../campaigns/templates.js';

function assetTitle(data: Record<string, unknown>, fallback: string): string {
  const t = data['title'] ?? data['name'];
  return typeof t === 'string' && t.length > 0 ? t : fallback;
}

/**
 * "Review, then publish" quick-launch (§8, §20.4) — the reusable form of the
 * approval-gated publish workflow. Pick an asset and a channel; it assembles the
 * review→share DAG and drops you on the campaign so you can run it. No privileged
 * path: it posts the same campaign body the visual builder would.
 */
function ReviewPublishModal({
  workspaceId,
  onClose,
}: {
  workspaceId: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  // Any publishable asset, not just active ones — reviewing before publish is the
  // safe way to push a draft, so drafts must be selectable here (archived aside).
  const assets = useAssets(workspaceId, { includeArchived: false });
  const destinations = useDestinations(workspaceId);
  const configs = useConnectorConfigs(workspaceId);
  const createCampaign = useCreateCampaign(workspaceId);

  const [assetId, setAssetId] = useState('');
  const [destinationId, setDestinationId] = useState('');
  const [name, setName] = useState('');

  const configName = (id: string): string =>
    configs.data?.data.find((c) => c.id === id)?.displayName ?? id;

  const submit = (): void => {
    if (assetId === '' || destinationId === '') return;
    const body = REVIEW_PUBLISH_TEMPLATE.build(
      { assetId, destinationId },
      name.trim() === '' ? 'Review, then publish' : name.trim(),
    );
    createCampaign.mutate(body, {
      onSuccess: (campaign) => {
        onClose();
        navigate(`/w/${workspaceId}/campaigns/${campaign.id}`);
      },
    });
  };

  const assetList = assets.data?.data ?? [];
  const destinationList = destinations.data?.data ?? [];

  return (
    <Modal
      open
      title="Review, then publish"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={createCampaign.isPending}
            disabled={assetId === '' || destinationId === ''}
          >
            Create campaign
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <p className="text-sm text-ink-muted">
          {REVIEW_PUBLISH_TEMPLATE.description} You&rsquo;ll land on the campaign to run it — it
          pauses at the review gate, and approving there publishes the post.
        </p>

        <Field label="Asset to publish" htmlFor="tmpl-asset">
          <Select id="tmpl-asset" value={assetId} onChange={(e) => setAssetId(e.target.value)}>
            <option value="">Choose an asset…</option>
            {assetList.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {assetTitle(asset.data, asset.id)} · {asset.type}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Publish to" htmlFor="tmpl-dest">
          <Select
            id="tmpl-dest"
            value={destinationId}
            onChange={(e) => setDestinationId(e.target.value)}
          >
            <option value="">Choose a channel…</option>
            {destinationList.map((dest) => (
              <option key={dest.id} value={dest.id}>
                {configName(dest.connectorConfigId)}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Campaign name"
          htmlFor="tmpl-name"
          hint="Optional — a default is used if blank."
        >
          <Input
            id="tmpl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Review, then publish"
          />
        </Field>

        {destinationList.length === 0 && !destinations.isLoading && (
          <Alert tone="warning" title="No channels yet">
            Add a destination on the Connectors page first — that&rsquo;s where an approved post
            goes.
          </Alert>
        )}
        {createCampaign.error instanceof Error && (
          <Alert tone="error" title="Could not create the campaign">
            {createCampaign.error.message}
          </Alert>
        )}
      </Stack>
    </Modal>
  );
}

/** Provenance + shape of a campaign — the same drawer every object opens. */
function campaignInspectorFields(campaign: Campaign): InspectorField[] {
  const fields: InspectorField[] = [
    { label: 'State', value: campaign.state },
    { label: 'Steps', value: `${String(campaign.dag.nodes.length)} in the graph` },
    {
      label: 'Schedule',
      value:
        campaign.schedule === null
          ? 'Not scheduled — runs on demand'
          : `${new Date(campaign.schedule.targetStartTime).toLocaleString()} (${campaign.schedule.state})`,
    },
    { label: 'Created', value: new Date(campaign.createdAt).toLocaleString() },
    { label: 'Campaign id', value: <span className="font-mono text-xs">{campaign.id}</span> },
  ];
  return fields;
}

/**
 * Campaign list (§20.4). Authoring happens on the visual builder at
 * `campaigns/new` (a drag-and-connect DAG canvas); this screen lists what
 * exists and links into each campaign's execution monitor.
 *
 * Data access is exclusively through hooks (§21.4); this component issues no
 * requests of its own.
 */

/** Campaign lifecycle states (§8) mapped onto the shared Badge tones. */
const CAMPAIGN_STATE_TONE: Record<string, Tone> = {
  draft: 'neutral',
  scheduled: 'neutral',
  running: 'info',
  awaiting_approval: 'warning',
  completed: 'success',
  failed: 'error',
  rejected: 'warning',
  cancelled: 'warning',
};

/** A status dot colour so state reads at a glance, before the label. */
const STATE_DOT: Record<string, string> = {
  draft: 'bg-edge-strong',
  scheduled: 'bg-navy-400',
  running: 'bg-state-info',
  awaiting_approval: 'bg-state-warning',
  completed: 'bg-state-success',
  failed: 'bg-state-error',
  rejected: 'bg-state-warning',
  cancelled: 'bg-edge-strong',
};

export function CampaignsPage() {
  const navigate = useNavigate();
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const { data, isLoading, error } = useCampaigns(workspaceId);

  const openBuilder = (): void => navigate(`/w/${workspaceId}/campaigns/new`);
  const [inspecting, setInspecting] = useState<Campaign | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);

  // The top-bar "Publish" and the command palette deep-link here to start a
  // publish, so open the launcher when we arrive with ?launch=review-publish and
  // consume the param so a refresh doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('launch') === 'review-publish') {
      setTemplateOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('launch');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const stepCount = (campaign: Campaign): number => campaign.dag.nodes.length;

  const campaignList = data?.data ?? [];
  const stateCount = (state: string): number =>
    campaignList.filter((c) => c.state === state).length;

  const columns: readonly Column<Campaign>[] = [
    {
      key: 'name',
      header: 'Campaign',
      sortValue: (campaign) => campaign.name.toLowerCase(),
      render: (campaign) => (
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(`/w/${workspaceId}/campaigns/${campaign.id}`)}
            className="block truncate text-left text-sm font-semibold text-navy-700 hover:text-navy-500"
          >
            {campaign.name}
          </button>
          <p className="text-xs text-ink-muted">
            {stepCount(campaign)} step{stepCount(campaign) === 1 ? '' : 's'} ·{' '}
            {new Date(campaign.createdAt).toLocaleDateString()}
          </p>
        </div>
      ),
    },
    {
      key: 'state',
      header: 'Status',
      sortValue: (campaign) => campaign.state,
      render: (campaign) => (
        <span className="inline-flex items-center gap-2">
          <span
            className={cx(
              'h-2 w-2 shrink-0 rounded-full',
              STATE_DOT[campaign.state] ?? 'bg-edge-strong',
            )}
            aria-hidden="true"
          />
          <Badge tone={CAMPAIGN_STATE_TONE[campaign.state] ?? 'neutral'} dot>
            {campaign.state.replace(/_/g, ' ')}
          </Badge>
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (campaign) => (
        <Cluster gap="md" justify="end">
          <button
            type="button"
            onClick={() => navigate(`/w/${workspaceId}/campaigns/${campaign.id}`)}
            className="text-sm font-semibold text-navy-700 hover:underline"
          >
            Open
          </button>
          <button
            type="button"
            onClick={() => setInspecting(campaign)}
            className="text-sm text-ink-muted hover:text-navy-700 hover:underline"
          >
            Inspect
          </button>
        </Cluster>
      ),
    },
  ];

  return (
    <Page
      title="Campaigns"
      icon={CampaignIcon}
      description="Your launches and their live status, each running review → publish → verify in order with an approval gate wherever you set one."
      stats={
        campaignList.length > 0 ? (
          <StatStrip
            stats={[
              { icon: CampaignIcon, label: 'Campaigns', value: campaignList.length },
              { icon: ClockIcon, label: 'Running', value: stateCount('running') },
              {
                icon: BellIcon,
                label: 'Needs approval',
                value: stateCount('awaiting_approval'),
                tone: 'accent',
              },
              { icon: CheckCircleIcon, label: 'Completed', value: stateCount('completed') },
            ]}
          />
        ) : undefined
      }
      actions={
        <Cluster gap="sm">
          <Button variant="secondary" onClick={() => setTemplateOpen(true)}>
            Review → publish
          </Button>
          <Button onClick={openBuilder}>New campaign</Button>
        </Cluster>
      }
    >
      <Stack gap="lg">
        <QueryState loading={isLoading} error={error} what="campaigns">
          {data !== undefined && data.data.length === 0 ? (
            <EmptyState
              icon={CampaignIcon}
              title="No campaigns yet"
              description="Start from the Review → publish template — pick an asset and a channel and it's ready to run — or open the visual builder to wire a launch by hand."
              features={[
                { icon: RocketIcon, label: 'Launch in two clicks from a template' },
                { icon: ShieldIcon, label: 'Every step pauses at an approval gate' },
                { icon: CheckCircleIcon, label: 'Live status as each step runs' },
              ]}
              action={
                <Cluster gap="sm">
                  <Button onClick={() => setTemplateOpen(true)}>Review → publish</Button>
                  <Button variant="secondary" onClick={openBuilder}>
                    Open the builder
                  </Button>
                </Cluster>
              }
            />
          ) : (
            <Stack gap="lg">
              {(() => {
                const list = data?.data ?? [];
                const n = (state: string): number => list.filter((c) => c.state === state).length;
                const running = n('running');
                const awaiting = n('awaiting_approval');
                const completed = n('completed');
                const drafts = n('draft') + n('scheduled');
                const failed = n('failed') + n('rejected') + n('cancelled');
                return (
                  <Card
                    title="Pipeline at a glance"
                    subtitle="Every launch runs review → publish → verify in order, pausing at each approval gate."
                  >
                    <Stack gap="md">
                      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <Metric label="Campaigns" value={list.length} />
                        <Metric label="Running" value={running} accent={running > 0} />
                        <Metric label="Needs approval" value={awaiting} accent={awaiting > 0} />
                        <Metric label="Completed" value={completed} />
                      </div>
                      <SegmentBar
                        segments={[
                          { label: 'Draft / scheduled', value: drafts, series: 'muted' },
                          { label: 'Running', value: running, series: 'info' },
                          { label: 'Awaiting approval', value: awaiting, series: 'warning' },
                          { label: 'Completed', value: completed, series: 'success' },
                          { label: 'Failed / rejected', value: failed, series: 'error' },
                        ]}
                      />
                    </Stack>
                  </Card>
                );
              })()}
              <Card>
                <Table
                  columns={columns}
                  rows={data?.data ?? []}
                  rowKey={(campaign) => campaign.id}
                  empty={
                    <EmptyState
                      icon={CampaignIcon}
                      title="No campaigns"
                      features={[
                        { icon: CampaignIcon, label: 'Sequenced review, publish & verify steps' },
                        { icon: BellIcon, label: 'Approvals surfaced where you set a gate' },
                        { icon: ClockIcon, label: 'Live run status at a glance' },
                      ]}
                    />
                  }
                />
              </Card>
            </Stack>
          )}
        </QueryState>
      </Stack>

      {templateOpen && (
        <ReviewPublishModal workspaceId={workspaceId} onClose={() => setTemplateOpen(false)} />
      )}

      <Inspector
        open={inspecting !== null}
        title={inspecting?.name ?? 'Campaign'}
        {...(inspecting !== null ? { subtitle: 'Campaign · provenance & shape' } : {})}
        onClose={() => setInspecting(null)}
        fields={inspecting !== null ? campaignInspectorFields(inspecting) : []}
      />
    </Page>
  );
}
